/**
 * trailStyle.ts — the Trail slot (lane BD, decision D46).
 *
 * Player ghosts have no feet (lane BC), so the item slot the saves and the
 * wire still call `shoes` / `feet` is presented as **Trail**: the equipped
 * item styles the wisp's light-mote trail (GhostBodyFx). The slot key never
 * changed — no migration, no protocol change. Each Trail item authors its
 * look under `[<item>.trail]` in content TOML (compile-content validates it),
 * so a new trail is data only.
 *
 * Remote clients already receive every equipped item id (the `equipment`
 * schema subtree → `player.equipmentVisuals`), so the same lookup styles the
 * local player's trail and everyone else's.
 */
import type { ResolvedPlayerEquipmentVisuals, EquipmentVisualResolution } from './equipmentVisuals';
import { ITEM_DATABASE } from './content/generated/items.gen';

/** The internal item slot the Trail is stored in (saves, wire, content). */
export const TRAIL_ITEM_SLOT = 'shoes' as const;
/** The schema/body slot the Trail is stored in (`player.equipment.feet`). */
export const TRAIL_SCHEMA_SLOT = 'feet' as const;

/**
 * One trail look. Authored in TOML as `color`/`glow`/`size`/`count`/`rise`.
 * Cheap by construction: the renderer keeps one fixed mote pool and only
 * tints, scales and fades it.
 */
export interface ItemTrailStyle {
  /** Mote tint, `#rgb` or `#rrggbb`. */
  color: string;
  /** Peak alpha of the first mote, 0.2–1. */
  glow: number;
  /** Diameter of the first mote in px, 8–26; later motes taper. */
  size: number;
  /** Visible motes, 1–TRAIL_MAX_MOTES. */
  count: number;
  /** Px a mote drifts up per sample of age; negative sinks, 0 lies flat. −4–8. */
  rise: number;
}

/** The renderer's fixed pool size — `count` can never exceed it. */
export const TRAIL_MAX_MOTES = 5;

/** The bounds compile-content enforces (scripts/content-schemas.ts mirrors them). */
export const TRAIL_STYLE_LIMITS = {
  glow: [0.2, 1],
  size: [8, 26],
  count: [1, TRAIL_MAX_MOTES],
  rise: [-4, 8],
} as const;

/** No Trail item equipped: today's pale-blue motes (lane AZ). */
export const DEFAULT_TRAIL_STYLE: Readonly<ItemTrailStyle> = Object.freeze({
  color: '#d8f4ff', glow: 0.75, size: 18, count: 5, rise: 3,
});

const HEX = /^#(?:[0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;
const within = (v: unknown, [lo, hi]: readonly [number, number]) =>
  typeof v === 'number' && Number.isFinite(v) && v >= lo && v <= hi;

/** Whether a trail style is renderable (the same contract the compiler enforces). */
export function isValidTrailStyle(s: unknown): s is ItemTrailStyle {
  if (!s || typeof s !== 'object') return false;
  const t = s as Record<string, unknown>;
  return typeof t.color === 'string' && HEX.test(t.color)
    && within(t.glow, TRAIL_STYLE_LIMITS.glow)
    && within(t.size, TRAIL_STYLE_LIMITS.size)
    && Number.isInteger(t.count) && within(t.count, TRAIL_STYLE_LIMITS.count)
    && within(t.rise, TRAIL_STYLE_LIMITS.rise);
}

/** The authored trail of an item, or null (unknown id, not a Trail item, corrupt style). */
export function trailStyleForItemId(itemId: string | null | undefined): ItemTrailStyle | null {
  if (!itemId) return null;
  const item = ITEM_DATABASE[itemId];
  if (!item || item.equipmentSlot !== TRAIL_ITEM_SLOT) return null;
  return isValidTrailStyle(item.trail) ? item.trail : null;
}

function resolutionItemId(r: EquipmentVisualResolution | undefined): string | null {
  if (!r || r.kind === 'empty') return null;
  return r.kind === 'resolved' ? r.descriptor.itemId : r.itemId;
}

/** The item id in a player's Trail slot, from the equipment projection every client has. */
export function equippedTrailItemId(visuals: ResolvedPlayerEquipmentVisuals | undefined | null): string | null {
  return resolutionItemId(visuals?.[TRAIL_SCHEMA_SLOT]);
}

/** The trail a player's ghost should draw: their Trail item's, else the default. */
export function trailStyleForEquipment(visuals: ResolvedPlayerEquipmentVisuals | undefined | null): Readonly<ItemTrailStyle> {
  return trailStyleForItemId(equippedTrailItemId(visuals)) ?? DEFAULT_TRAIL_STYLE;
}

/** `#rgb`/`#rrggbb` → 0xRRGGBB for ISprite.setTint. */
export function trailTint(color: string): number {
  const hex = color.slice(1);
  const full = hex.length === 3 ? hex.split('').map(c => c + c).join('') : hex;
  return parseInt(full, 16);
}
